/**
 * ParticipantsDashboard Component
 * Displays participants grouped by type: Milestone, Program, Network Partner
 * Shows funding allocation and payout tracking
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  Target,
  Calendar,
  Network,
  Users,
  DollarSign,
  CheckCircle2,
  Clock,
  Building2,
  User,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

interface Participant {
  id: number;
  participant_type: string;
  participant_name: string;
  participant_email: string;
  cbo_id?: number;
  individual_id?: number;
  program_id?: number;
  program_name?: string;
  participation_type: string;
  status: string;
  agreement_date: string;
  start_date?: string;
  end_date?: string;
  total_funding_allocated: number;
  total_paid: number;
  payment_terms: string;
  is_credentialed: boolean;
  credential_type?: string;
  milestone_count: number;
  notes?: string;
}

interface ParticipationStats {
  milestone_participants: number;
  program_participants: number;
  network_partners: number;
  active_participants: number;
  total_funding_allocated: number;
  total_paid: number;
}

interface ParticipantsDashboardProps {
  sponsorId: number;
  programId?: number;
}

export const ParticipantsDashboard: React.FC<ParticipantsDashboardProps> = ({
  sponsorId,
  programId
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [stats, setStats] = useState<ParticipationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadParticipants();
    loadStats();
  }, [sponsorId, programId]);

  const loadParticipants = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = getWeaveinApiUrl(`/api/sponsors/${sponsorId}/participants`);
      if (programId) {
        url += `?program_id=${programId}`;
      }

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error('Failed to load participants');
      }

      const data = await response.json();
      setParticipants(data);
    } catch (err) {
      console.error('Error loading participants:', err);
      setError(err instanceof Error ? err.message : 'Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/sponsors/${sponsorId}/participation-stats`));

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode }> = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-3 h-3" /> },
      ACTIVE: { color: 'bg-green-100 text-green-800', icon: <CheckCircle2 className="w-3 h-3" /> },
      COMPLETED: { color: 'bg-blue-100 text-blue-800', icon: <CheckCircle2 className="w-3 h-3" /> },
      SUSPENDED: { color: 'bg-orange-100 text-orange-800', icon: <AlertCircle className="w-3 h-3" /> },
      TERMINATED: { color: 'bg-red-100 text-red-800', icon: <AlertCircle className="w-3 h-3" /> }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: null };

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'MILESTONE':
        return <Target className="w-4 h-4 text-blue-600" />;
      case 'PROGRAM':
        return <Calendar className="w-4 h-4 text-green-600" />;
      case 'NETWORK_PARTNER':
        return <Network className="w-4 h-4 text-purple-600" />;
      default:
        return null;
    }
  };

  const filterParticipants = (type: string) => {
    if (type === 'all') return participants;
    return participants.filter(p => p.participation_type === type);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">{t('sponsors.loading_participants', 'Loading participants...')}</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 border-red-200 bg-red-50">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.milestone_participants}</div>
                <div className="text-xs text-gray-500">{t('sponsors.milestone', 'Milestone')}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.program_participants}</div>
                <div className="text-xs text-gray-500">{t('sponsors.program', 'Program')}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Network className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.network_partners}</div>
                <div className="text-xs text-gray-500">{t('sponsors.network_partners', 'Network Partners')}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-lg font-bold">{formatCurrency(stats.total_paid)}</div>
                <div className="text-xs text-gray-500">
                  of {formatCurrency(stats.total_funding_allocated)}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Participants List */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('sponsors.participants', 'Participants')}</h3>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="all">
              All ({participants.length})
            </TabsTrigger>
            <TabsTrigger value="MILESTONE">
              Milestone ({filterParticipants('MILESTONE').length})
            </TabsTrigger>
            <TabsTrigger value="PROGRAM">
              Program ({filterParticipants('PROGRAM').length})
            </TabsTrigger>
            <TabsTrigger value="NETWORK_PARTNER">
              Network ({filterParticipants('NETWORK_PARTNER').length})
            </TabsTrigger>
          </TabsList>

          {['all', 'MILESTONE', 'PROGRAM', 'NETWORK_PARTNER'].map(tabValue => (
            <TabsContent key={tabValue} value={tabValue} className="space-y-3">
              {filterParticipants(tabValue).length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">{t('sponsors.no_participants_in_this_category', 'No participants in this category')}</p>
                </div>
              ) : (
                filterParticipants(tabValue).map(participant => (
                  <Card key={participant.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {participant.participant_type === 'CBO' ? (
                            <Building2 className="w-4 h-4 text-gray-400" />
                          ) : (
                            <User className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="font-medium text-gray-900">
                            {participant.participant_name}
                          </span>
                          {getTypeIcon(participant.participation_type)}
                        </div>

                        {participant.program_name && (
                          <p className="text-sm text-gray-600 mt-1">
                            {participant.program_name}
                          </p>
                        )}

                        <div className="flex items-center gap-3 mt-2">
                          {getStatusBadge(participant.status)}
                          {participant.is_credentialed && (
                            <Badge className="bg-purple-100 text-purple-800">
                              {t('sponsors.credentialed', 'Credentialed')}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          {formatCurrency(participant.total_paid)}
                        </div>
                        <div className="text-xs text-gray-500">
                          of {formatCurrency(participant.total_funding_allocated)}
                        </div>
                        <button
                          onClick={() => setExpandedId(
                            expandedId === participant.id ? null : participant.id
                          )}
                          className="mt-2 p-1 hover:bg-gray-100 rounded"
                        >
                          {expandedId === participant.id ? (
                            <ChevronUp className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedId === participant.id && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">{t('sponsors.agreement_date', 'Agreement Date')}</span>
                            <div className="font-medium">
                              {participant.agreement_date
                                ? fmt.short(participant.agreement_date)
                                : 'N/A'}
                            </div>
                          </div>
                          {participant.start_date && (
                            <div>
                              <span className="text-gray-500">{t('sponsors.start_date', 'Start Date')}</span>
                              <div className="font-medium">
                                {fmt.short(participant.start_date)}
                              </div>
                            </div>
                          )}
                          {participant.end_date && (
                            <div>
                              <span className="text-gray-500">{t('sponsors.end_date', 'End Date')}</span>
                              <div className="font-medium">
                                {fmt.short(participant.end_date)}
                              </div>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">{t('sponsors.payment_terms', 'Payment Terms')}</span>
                            <div className="font-medium">{participant.payment_terms || 'N/A'}</div>
                          </div>
                        </div>

                        {participant.participation_type === 'MILESTONE' && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <div className="text-sm font-medium text-blue-900">
                              {participant.milestone_count} milestones enrolled
                            </div>
                          </div>
                        )}

                        {participant.notes && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <span className="text-xs text-gray-500">{t('sponsors.notes', 'Notes')}</span>
                            <p className="text-sm text-gray-700">{participant.notes}</p>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            {t('sponsors.view_details', 'View Details')}
                          </Button>
                          <Button variant="outline" size="sm">
                            <DollarSign className="w-4 h-4 mr-1" />
                            {t('sponsors.create_payout', 'Create Payout')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </TabsContent>
          ))}
        </Tabs>
      </Card>
    </div>
  );
};

export default ParticipantsDashboard;
